import { FormSectionCard } from '@/components/tax-form/FormSectionCard';
import {
  BUSINESS_FIELDS,
  BUSINESS_SECTION_KEYS,
  BUSINESS_SECTIONS,
  type FieldMeta,
} from '@/components/tax-form/businessFormConfig';
import {
  getBusinessSelectOptions,
  getStateOptions,
  getNestedValue,
  setNestedValue,
} from '@/components/tax-form/formFieldHelpers';
import { DatePickerInput } from '@/components/ui/date-picker';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import type { BusinessFormData } from '@/types/form';
import { Plus, Trash2 } from 'lucide-react';

function SelectField({
  label,
  value,
  onChange,
  options,
  placeholder = 'Select…',
}: {
  label: string;
  value?: string;
  onChange: (v: string) => void;
  options: { value: string; label: string }[];
  placeholder?: string;
}) {
  return (
    <div className="space-y-2">
      <Label>{label}</Label>
      <Select value={value ?? ''} onValueChange={onChange}>
        <SelectTrigger>
          <SelectValue placeholder={placeholder} />
        </SelectTrigger>
        <SelectContent>
          {options.map((o) => (
            <SelectItem key={o.value} value={o.value}>
              {o.label}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    </div>
  );
}

const PARTNERSHIP_BUSINESS_TYPES = ['PARTNERSHIP', 'S_CORP'];

function renderField(
  fieldMeta: FieldMeta,
  value: unknown,
  onChange: (v: unknown) => void,
  optionsGetter: (key: string) => { value: string; label: string }[]
) {
  const { key, label, placeholder, type = 'text', fullWidth, optionsKey, dateFormat } = fieldMeta;
  const val = getNestedValue(value, key);
  const strVal = val != null && typeof val !== 'boolean' ? String(val) : '';
  const numVal = typeof val === 'number' ? val : '';
  const boolVal = !!val;

  const className = fullWidth ? 'space-y-2 sm:col-span-2' : 'space-y-2';

  if (type === 'checkbox') {
    return (
      <div key={key} className={className}>
        <div className="flex items-center space-x-2">
          <Checkbox
            id={key}
            checked={boolVal}
            onCheckedChange={(c) => onChange(setNestedValue(value, key, !!c))}
          />
          <Label htmlFor={key}>{label}</Label>
        </div>
      </div>
    );
  }

  if (type === 'select') {
    const options = optionsKey ? optionsGetter(optionsKey) : [];
    return (
      <div key={key} className={className}>
        <SelectField
          label={label}
          value={strVal}
          onChange={(v) => onChange(setNestedValue(value, key, v))}
          options={options}
          placeholder={placeholder}
        />
      </div>
    );
  }

  if (type === 'stateSelect') {
    const options = getStateOptions();
    return (
      <div key={key} className={className}>
        <SelectField
          label={label}
          value={strVal}
          onChange={(v) => onChange(setNestedValue(value, key, v))}
          options={options}
          placeholder={placeholder}
        />
      </div>
    );
  }

  if (type === 'date') {
    return (
      <div key={key} className={className}>
        <DatePickerInput
          label={label}
          value={strVal}
          onChange={(v) => onChange(setNestedValue(value, key, v))}
          placeholder={placeholder ?? 'MM/DD/YYYY'}
          fullWidth={fullWidth}
          monthYearOnly={dateFormat === 'monthYear'}
        />
      </div>
    );
  }

  const inputType = type === 'number' ? 'number' : 'text';
  return (
    <div key={key} className={className}>
      <Label>{label}</Label>
      <Input
        type={inputType}
        value={type === 'number' ? numVal : strVal}
        onChange={(e) => {
          const v = type === 'number' ? (e.target.value ? Number(e.target.value) : undefined) : e.target.value;
          onChange(setNestedValue(value, key, v));
        }}
        placeholder={placeholder}
      />
    </div>
  );
}

export interface BusinessFormSectionsProps {
  data: BusinessFormData;
  onChange: (data: BusinessFormData) => void;
  onSaveSection: (key: keyof BusinessFormData, payload: unknown) => Promise<void>;
  savingSection: string | null;
  currentSectionIndex: number;
  onNext: () => void;
  onPrev: () => void;
  isFirstSection: boolean;
  isLastSection: boolean;
}

export function BusinessFormSections({
  data,
  onChange,
  currentSectionIndex,
}: BusinessFormSectionsProps) {
  const optionsGetter = getBusinessSelectOptions;

  const sectionKey = BUSINESS_SECTION_KEYS[currentSectionIndex];
  if (!sectionKey) return null;

  const sectionMeta = BUSINESS_SECTIONS.find((s) => s.key === sectionKey);
  const fields = BUSINESS_FIELDS[sectionKey];
  if (!sectionMeta || !fields) return null;

  const value = data[sectionKey] ?? {};
  const sectionValue = typeof value === 'object' && value !== null ? value : {};
  const businessType = (data.businessDetails as { businessType?: string } | undefined)?.businessType;
  const isPartnershipType = businessType && PARTNERSHIP_BUSINESS_TYPES.includes(businessType);

  // Partnership section: "Add partner" button drives the list; optional text field for number of partners
  if (sectionKey === 'partnershipDetails') {
    const pd = (data.partnershipDetails ?? {}) as {
      numberOfPartners?: number | string;
      partnerInformation?: Array<{ name?: string; address?: { street?: string; city?: string; state?: string; zip?: string }; ssn?: string; partnershipPercentage?: number | string }>;
    };
    const partnerInfo = Array.isArray(pd.partnerInformation) ? [...pd.partnerInformation] : [];
    const setPartnership = (next: typeof pd) => {
      const numPartners =
        typeof next.numberOfPartners === 'string'
          ? next.numberOfPartners === ''
            ? undefined
            : Number(next.numberOfPartners)
          : next.numberOfPartners;
      const details = {
        ...next,
        numberOfPartners: numPartners,
        partnerInformation: next.partnerInformation ?? [],
      };
      onChange({
        ...data,
        partnershipDetails: details as BusinessFormData['partnershipDetails'],
      });
    };

    const addPartner = () => {
      const nextInfo = [...partnerInfo, {}];
      setPartnership({
        ...pd,
        partnerInformation: nextInfo,
        numberOfPartners: nextInfo.length,
      });
    };
    const removePartner = (index: number) => {
      const nextInfo = partnerInfo.filter((_, i) => i !== index);
      setPartnership({
        ...pd,
        partnerInformation: nextInfo,
        numberOfPartners: nextInfo.length,
      });
    };

    return (
      <FormSectionCard
        title={sectionMeta.title}
        description={sectionMeta.description}
      >
        <div className="space-y-6">
          {!isPartnershipType ? (
            <p className="text-muted-foreground text-sm">
              Complete Business Details and select Partnership (1065) or S-Corp (1120-S) to fill this section.
            </p>
          ) : (
            <>
              <div className="grid gap-4 sm:grid-cols-2 max-w-xs">
                <div className="space-y-2">
                  <Label>Number of partners</Label>
                  <Input
                    type="text"
                    value={pd.numberOfPartners != null ? String(pd.numberOfPartners) : ''}
                    onChange={(e) => {
                      const raw = e.target.value.trim();
                      const num = raw === '' ? 0 : Math.max(0, Math.floor(Number(raw)) || 0);
                      const nextInfo = [...partnerInfo];
                      while (nextInfo.length < num) nextInfo.push({});
                      if (nextInfo.length > num) nextInfo.length = num;
                      setPartnership({
                        ...pd,
                        numberOfPartners: raw === '' ? undefined : num,
                        partnerInformation: nextInfo,
                      });
                    }}
                    placeholder="0"
                  />
                </div>
              </div>
              <div className="flex flex-wrap items-center gap-4">
                <Button type="button" variant="outline" size="sm" onClick={addPartner}>
                  <Plus className="h-4 w-4 mr-2" />
                  Add partner
                </Button>
                {partnerInfo.length > 0 && (
                  <span className="text-sm text-muted-foreground">
                    {partnerInfo.length} partner{partnerInfo.length !== 1 ? 's' : ''}
                  </span>
                )}
              </div>
              {partnerInfo.length > 0 && (
                <div className="space-y-6 border-t pt-6">
                  {partnerInfo.map((_, i) => {
                    const sectionVal = { numberOfPartners: partnerInfo.length, partnerInformation: partnerInfo };
                    const updateSection = (newSection: unknown) =>
                      setPartnership(newSection as typeof pd);
                    return (
                      <div key={i} className="rounded-lg border p-4 space-y-4">
                        <div className="flex items-center justify-between">
                          <h4 className="font-medium text-sm">Partner {i + 1}</h4>
                          <Button
                            type="button"
                            variant="ghost"
                            size="sm"
                            className="text-destructive hover:text-destructive"
                            onClick={() => removePartner(i)}
                          >
                            <Trash2 className="h-4 w-4 mr-1" />
                            Remove
                          </Button>
                        </div>
                        <div className="grid gap-4 sm:grid-cols-2">
                          {[
                            { key: `partnerInformation.${i}.name`, label: 'Name', placeholder: 'Partner Name', fullWidth: true },
                            { key: `partnerInformation.${i}.address.street`, label: 'Address — Street', placeholder: 'Street', fullWidth: true },
                            { key: `partnerInformation.${i}.address.city`, label: 'City', placeholder: 'City' },
                            { key: `partnerInformation.${i}.address.state`, label: 'State', type: 'stateSelect' as const },
                            { key: `partnerInformation.${i}.address.zip`, label: 'ZIP', placeholder: 'ZIP' },
                            { key: `partnerInformation.${i}.ssn`, label: 'SSN', placeholder: 'SSN' },
                            { key: `partnerInformation.${i}.partnershipPercentage`, label: 'Partnership %', type: 'text' as const, placeholder: '0' },
                          ].map((f) => {
                            const val = getNestedValue(sectionVal, f.key);
                            const strVal = val != null && typeof val !== 'boolean' ? String(val) : '';
                            const className = f.fullWidth ? 'space-y-2 sm:col-span-2' : 'space-y-2';
                            const onChangePartner = (v: unknown) =>
                              updateSection(setNestedValue(sectionVal, f.key, v));
                            if (f.type === 'stateSelect') {
                              return (
                                <div key={f.key} className={className}>
                                  <SelectField
                                    label={f.label}
                                    value={strVal}
                                    onChange={(v) => onChangePartner(v)}
                                    options={getStateOptions()}
                                    placeholder={f.placeholder}
                                  />
                                </div>
                              );
                            }
                            return (
                              <div key={f.key} className={className}>
                                <Label>{f.label}</Label>
                                <Input
                                  type="text"
                                  value={strVal}
                                  onChange={(e) => onChangePartner(e.target.value)}
                                  placeholder={f.placeholder}
                                />
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </>
          )}
        </div>
      </FormSectionCard>
    );
  }

  return (
    <FormSectionCard
      title={sectionMeta.title}
      description={sectionMeta.description}
    >
      <div className="grid gap-4 sm:grid-cols-2">
        {fields
          .filter((f) => !f.showWhenBusinessType || (businessType && f.showWhenBusinessType.includes(businessType)))
          .map((field) =>
            renderField(
              field,
              sectionValue,
              (v) => onChange({ ...data, [sectionKey]: v }),
              optionsGetter
            )
          )}
      </div>
    </FormSectionCard>
  );
}
